/**
 * Клиент HTTP API.
 *
 * Все запросы идут на тот же origin, что и страница: в dev это обеспечивает
 * прокси Vite, в production — Nginx. Исключение — probe зарубежной точки: её
 * адрес приходит в плане проверок и ведёт на другой домен (см. CheckPlan).
 */

import type { ClientResults, ReportResponse, SessionResponse } from '@vpncheck/shared';

/** Таймаут на обычный запрос к API. Probe-запросы задают свой. */
const API_TIMEOUT_MS = 10_000;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // content-type ставится только при наличии тела. С заголовком, но без тела
  // Fastify отвечает FST_ERR_CTP_EMPTY_JSON_BODY: он ждёт JSON, раз его обещали.
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...init.headers,
    },
    signal: init.signal ?? AbortSignal.timeout(API_TIMEOUT_MS),
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new ApiError(await errorMessage(response), response.status);
  }
  return (await response.json()) as T;
}

/** Достаёт текст ошибки из тела ответа, не падая на не-JSON. */
async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string') return body.error;
  } catch {
    // тело не JSON — используем статус
  }
  return `Сервер ответил ${response.status}`;
}

export function createSession(): Promise<SessionResponse> {
  return request<SessionResponse>('/api/session', { method: 'POST' });
}

export function submitClientResults(token: string, results: ClientResults): Promise<void> {
  return request<void>(`/api/session/${token}/client`, {
    method: 'POST',
    body: JSON.stringify(results),
  });
}

export function fetchReport(token: string): Promise<ReportResponse> {
  return request<ReportResponse>(`/api/report/${token}`);
}
